<p-progressBar mode="indeterminate" *ngIf="isLoading || isSaving"></p-progressBar>
<p-toast></p-toast>

<app-page-shell>
  <app-page-hero
    eyebrow="Warehousing"
    [title]="inboundRequest ? 'Receive Shipment Cargo' : 'New Goods In'"
    [subtitle]="inboundRequest ? 'Confirm the physical count and issue a Goods Receipt Note (GRN).' : 'Receive multiple items in one warehouse and issue a Goods Receipt Note (GRN).'">
    <div actions class="flex flex-wrap gap-2">
      <p-button label="Back to Receipts" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/warehousing/stock-entry"></p-button>
      <p-button [label]="inboundRequest ? 'Confirm inbound count' : 'Submit & Issue GRN'" icon="pi pi-check" severity="success" [loading]="isSaving" [disabled]="!!inboundRequest && !linesArray.length" (click)="submitBulkReceiving()"></p-button>
    </div>
  </app-page-hero>

  <form [formGroup]="bulkForm" class="bulk-receiving-form p-fluid">
    @if (inboundRequest; as request) {
      <div class="surface-50 border-1 surface-border border-round p-3">
        <strong>{{ request.shipmentNumber }}</strong> · Warehouse #{{ request.warehouseId }}
        <p class="my-2 text-600">Only completed destination stop quantities are listed. Enter the actual count; zero and shortages require an explanation.</p>
        @if (request.possibleManualReceipts.length) {
          <div class="surface-card border-1 surface-border border-round p-3">
            <strong>Possible earlier manual receipts</strong>
            <ul class="my-2">
              @for (receipt of request.possibleManualReceipts; track receipt.id) {
                <li>{{ receipt.entryNumber }} · Item #{{ receipt.itemId }} · {{ receipt.quantity }} units · {{ receipt.entryDate | date:'mediumDate' }}</li>
              }
            </ul>
            <label class="flex align-items-center gap-2"><p-checkbox [(ngModel)]="reviewedManualReceipts" [ngModelOptions]="{ standalone: true }" [binary]="true"></p-checkbox> I reviewed these receipts and confirmed this cargo still needs receiving.</label>
          </div>
        }
      </div>
    }
    <!-- Consignment & Receipt Header Section -->
    <app-page-form-section
      icon="pi pi-truck"
      title="Goods In Note"
      subtitle="Choose the warehouse, date, and optional delivery reference.">
      <div class="formgrid grid">
        <div class="field col-12 md:col-4">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="bulkRefNumber" pInputText formControlName="referenceNumber" placeholder="DN-XXXX or ASN-XXXX">
              <label for="bulkRefNumber">Delivery Note / Reference #</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>

        <div class="field col-12 md:col-4">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select id="bulkWarehouse" [options]="masterData.WAREHOUSE" optionLabel="name" optionValue="id" formControlName="warehouseId" placeholder="Select Warehouse" appendTo="body" styleClass="w-full"></p-select>
              <label for="bulkWarehouse">Receiving Warehouse *</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>

        <div class="field col-12 md:col-4">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-datePicker id="bulkReceiptDate" formControlName="receiptDate" dateFormat="yy-mm-dd" [showIcon]="false" [appendTo]="'body'" styleClass="w-full"></p-datePicker>
              <label for="bulkReceiptDate">Receipt Date *</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>

        @if (!inboundRequest) { <div class="field col-12">
          <p-button [label]="showMoreDetails ? 'Hide More Details' : 'More Details'" icon="pi pi-ellipsis-h"
            severity="secondary" [outlined]="true" type="button" (click)="showMoreDetails = !showMoreDetails"></p-button>
        </div> }
        @if (showMoreDetails && !inboundRequest) {
        <div class="field col-12 md:col-6">
          <div class="flex gap-2 align-items-center">
            <p-inputGroup class="flex-1">
              <p-inputGroupAddon><i class="pi pi-truck"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select id="bulkSupplier" [options]="clients" optionLabel="companyName" optionValue="id" formControlName="supplierId" [filter]="true" filterBy="companyName" [showClear]="true" placeholder="Select CRM Supplier" appendTo="body" styleClass="w-full"></p-select>
                <label for="bulkSupplier">Origin CRM Supplier</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button type="button" icon="pi pi-plus" label="Add" severity="secondary" [outlined]="true" size="small" (click)="quickSupplierVisible = true" [title]="'Create a supplier and select it for this receipt'"></p-button>
          </div>
        </div>

        <div class="field col-12 md:col-6">
          <div class="flex gap-2 align-items-center">
            <p-inputGroup class="flex-1">
              <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select id="bulkDriver" [options]="driverOptions()" optionLabel="displayLabel" optionValue="id" formControlName="driverId" [filter]="true" filterBy="displayLabel,legalName" [resetFilterOnHide]="true" [showClear]="true" [virtualScroll]="true" [virtualScrollItemSize]="36" [lazy]="true" [loading]="driversLoading" [emptyMessage]="driverLoadError ? 'Unable to load drivers' : 'No drivers found'" [emptyFilterMessage]="driverLoadError ? 'Unable to load drivers' : 'No matching drivers'" placeholder="Select Delivery Driver" appendTo="body" styleClass="w-full" (onShow)="loadDrivers('')" (onFilter)="loadDrivers($event.filter)" (onLazyLoad)="loadMoreDrivers($event)" (onChange)="onDriverChange($event.value)"></p-select>
                <label for="bulkDriver">Delivery Driver</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-button type="button" icon="pi pi-sparkles" label="AI Fleet Onboard" severity="secondary" [outlined]="true" size="small" (click)="openQuickFleetOnboarding()" [title]="'Quickly register driver & vehicle with AI document scan'"></p-button>
          </div>
        </div>

        <div class="field col-12">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <textarea id="bulkNotes" pTextarea formControlName="notes" rows="2" placeholder="Inspection findings, seal condition, driver notes..."></textarea>
              <label for="bulkNotes">Consignment & Inspection Notes</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
        <div class="field col-12">
          <div class="surface-50 border-1 surface-border border-round p-3">
            <div class="flex flex-wrap align-items-center gap-3">
              <label for="bulkDnFiles" class="font-medium"><i class="pi pi-paperclip"></i> Delivery Note attachments</label>
              <input id="bulkDnFiles" type="file" multiple accept="application/pdf,image/*" (change)="onDnFiles($event)">
              <span class="text-500 text-sm">Attach one or more DN files; they will be linked to the GRN after receipt.</span>
            </div>
            <div *ngIf="dnFiles.length" class="flex flex-wrap gap-2 mt-3">
              <p-button *ngFor="let file of dnFiles; let i = index" [label]="file.name" icon="pi pi-times" severity="secondary" size="small" [outlined]="true" (click)="removeDnFile(i)"></p-button>
            </div>
          </div>
        </div>
        }
      </div>
    </app-page-form-section>

    <!-- Manifested Items Table Section -->
    <app-page-form-section
      icon="pi pi-list-check"
      title="Received Manifest Items"
      subtitle="Specify the items, quantities, packaging units, and warehouse zones to place stock.">

      <div class="manifest-toolbar flex flex-wrap justify-content-between align-items-center mb-3">
        <div class="flex gap-3 align-items-center">
          <span class="font-bold text-teal-800"><i class="pi pi-box"></i> {{ totalItemsCount() }} Line(s)</span>
          <span class="font-bold text-indigo-800"><i class="pi pi-chart-pie"></i> Total Qty: {{ totalQuantitySum() | number:'1.0-2' }}</span>
        </div>
        @if (!inboundRequest) { <p-button label="Add Item Line" icon="pi pi-plus" size="small" severity="primary" (click)="addLine()"></p-button> }
      </div>

      <div class="manifest-table-scroll overflow-x-auto">
        <table class="w-full border-collapse surface-card border-round border-1 surface-border text-sm">
          <thead>
            <tr class="surface-100 text-700 font-semibold text-left">
              <th class="p-2 border-bottom-1 surface-border" style="width: 35px;">#</th>
              <th class="p-2 border-bottom-1 surface-border" style="min-width: 240px;">Item *</th>
              <th class="p-2 border-bottom-1 surface-border" style="min-width: 150px;">Zone *</th>
              <th class="p-2 border-bottom-1 surface-border" style="min-width: 130px;">Condition *</th>
              <th class="p-2 border-bottom-1 surface-border" style="width: 120px;">Qty *</th>
              <th class="p-2 border-bottom-1 surface-border" style="width: 100px;">{{ inboundRequest ? 'Disposition' : 'Packages' }}</th>
              <th class="p-2 border-bottom-1 surface-border" style="min-width: 160px;">Remarks</th>
              @if (!inboundRequest) { <th class="p-2 border-bottom-1 surface-border text-center" style="width: 80px;">Actions</th> }
            </tr>
          </thead>
          <tbody [formArrayName]="'lines'">
            <tr *ngFor="let line of linesArray.controls; let i = index" [formGroupName]="i" class="border-bottom-1 surface-border hover:surface-50">
              <td class="p-2 text-500 font-bold text-center">{{ i + 1 }}</td>
              <td class="p-2">
                <p-select [options]="items" optionLabel="displayLabel" optionValue="id" formControlName="itemId" [filter]="true" filterBy="displayLabel,skuNumber" placeholder="Select Item" appendTo="body" styleClass="w-full p-inputtext-sm"></p-select>
                <div *ngIf="selectedItem(line.get('itemId')?.value)" class="text-xs text-500 mt-1 flex gap-2">
                  <span><strong>SKU:</strong> {{ selectedItem(line.get('itemId')?.value)?.skuNumber || '-' }}</span>
                  <span><strong>UOM:</strong> {{ selectedItem(line.get('itemId')?.value)?.tenantUomName || 'PCS' }}</span>
                  <span *ngIf="selectedItem(line.get('itemId')?.value)?.categoryName"><strong>Cat:</strong> {{ selectedItem(line.get('itemId')?.value)?.categoryName }}</span>
                </div>
                @if (inboundRequest) { <small class="block mt-1 text-600">Owner: {{ inboundOwnerLabel(line.get('requestLineId')?.value) }}</small> }
                <textarea *ngIf="itemRequiresUnitTracking(selectedItem(line.get('itemId')?.value))" pTextarea formControlName="unitSkusText" rows="2" class="w-full mt-2 text-xs" placeholder="Optional known unit codes; missing WSU codes are generated automatically"></textarea>
              </td>
              <td class="p-2">
                <p-select [options]="availableZones()" optionLabel="name" optionValue="id" formControlName="zoneId" placeholder="Select Zone" appendTo="body" styleClass="w-full p-inputtext-sm"></p-select>
              </td>
              <td class="p-2">
                <p-select [options]="masterData.ITEM_CONDITION" optionLabel="name" optionValue="id" formControlName="conditionId" placeholder="Condition" appendTo="body" styleClass="w-full p-inputtext-sm"></p-select>
              </td>
              <td class="p-2">
                @if (!inboundRequest) { <p-select [options]="uomOptionsForItem(line.get('itemId')?.value)" optionLabel="label" optionValue="value" formControlName="transactionUomId" [showClear]="true" placeholder="Base UOM" appendTo="body" styleClass="w-full p-inputtext-sm mb-1"></p-select> }
                @if (inboundRequest) { <small class="text-600">Expected {{ line.get('expectedQuantity')?.value }}</small> }
                <p-inputNumber formControlName="quantity" [min]="inboundRequest ? 0 : 0.0001" [max]="inboundRequest ? line.get('expectedQuantity')?.value : undefined" [minFractionDigits]="0" [maxFractionDigits]="3" styleClass="w-full p-inputtext-sm"></p-inputNumber>
              </td>
              <td class="p-2">
                @if (inboundRequest) {
                  <p-select [options]="[{ label: 'Available', value: 'AVAILABLE' }, { label: 'Hold', value: 'HOLD' }]" optionLabel="label" optionValue="value" formControlName="disposition" appendTo="body" styleClass="w-full"></p-select>
                } @else { <p-inputNumber formControlName="packageQuantity" [min]="0" [maxFractionDigits]="0" placeholder="Pkgs" styleClass="w-full p-inputtext-sm"></p-inputNumber> }
              </td>
              <td class="p-2">
                @if (inboundRequest) { <input pInputText formControlName="exceptionReason" placeholder="Reason for shortage or hold" class="w-full p-inputtext-sm"> }
                @else { <input pInputText formControlName="remarks" placeholder="Line notes" class="w-full p-inputtext-sm"> }
              </td>
              @if (!inboundRequest) { <td class="p-2 text-center">
                <div class="flex gap-1 justify-content-center">
                  <p-button icon="pi pi-copy" severity="secondary" [text]="true" size="small" [title]="'Duplicate row'" (click)="duplicateLine(i)"></p-button>
                  <p-button icon="pi pi-trash" severity="danger" [text]="true" size="small" [title]="'Remove row'" (click)="removeLine(i)"></p-button>
                </div>
              </td> }
            </tr>
          </tbody>
        </table>
      </div>

      <div class="manifest-actions flex flex-wrap justify-content-end gap-2 mt-4">
        @if (!inboundRequest) { <p-button label="Add Another Item" icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="addLine()"></p-button> }
        <p-button [label]="inboundRequest ? 'Confirm inbound count' : 'Complete Bulk Receiving & Issue GRN'" icon="pi pi-check" severity="success" [loading]="isSaving" [disabled]="!!inboundRequest && !linesArray.length" (click)="submitBulkReceiving()"></p-button>
      </div>
    </app-page-form-section>
  </form>

  <!-- AI Quick Fleet Onboarding Dialog -->
  <app-quick-fleet-onboarding-dialog
    [(visible)]="quickFleetOnboardingVisible"
    [defaultSource]="'freelance'"
    (completed)="onQuickFleetOnboarded($event)">
  </app-quick-fleet-onboarding-dialog>

  <p-dialog header="Create supplier" [(visible)]="quickSupplierVisible" [modal]="true" appendTo="body" [style]="{ width: 'min(34rem, 94vw)' }">
    <form [formGroup]="quickSupplierForm" class="formgrid grid p-fluid" (ngSubmit)="createQuickSupplier()">
      <div class="field col-12"><p class="m-0 text-600">Creates a CRM client with the <strong>SUPPLIER</strong> role and selects it for this receipt.</p></div>
      <div class="field col-12"><p-iftaLabel><input id="quickSupplierName" pInputText formControlName="companyName"><label for="quickSupplierName">Supplier company name *</label></p-iftaLabel></div>
      <div class="field col-12 md:col-6"><p-iftaLabel><input id="quickSupplierEmail" pInputText type="email" formControlName="primaryEmail"><label for="quickSupplierEmail">Email</label></p-iftaLabel></div>
      <div class="field col-12 md:col-6"><p-iftaLabel><input id="quickSupplierPhone" pInputText formControlName="phoneNumber"><label for="quickSupplierPhone">Phone</label></p-iftaLabel></div>
      <div class="col-12 flex justify-content-end gap-2"><p-button type="button" label="Cancel" severity="secondary" [outlined]="true" (click)="quickSupplierVisible = false"></p-button><p-button type="submit" label="Create supplier" icon="pi pi-plus" [loading]="isCreatingSupplier"></p-button></div>
    </form>
  </p-dialog>

  <!-- Receipt Creation & GRN Print Modal Dialog -->
  <p-dialog
    header="Inbound Goods Receipt Note Issued"
    [(visible)]="successDialogVisible"
    [modal]="true"
    [closable]="false"
    appendTo="body"
    [style]="{ width: '480px' }">
    <div class="text-center p-3">
      <div class="inline-flex align-items-center justify-content-center border-circle bg-green-100 text-green-700 w-4rem h-4rem mb-3">
        <i class="pi pi-check-circle text-4xl"></i>
      </div>
      <h3 class="mt-0 mb-1 text-900 font-bold">Consignment Received Successfully!</h3>
      <p class="text-600 mb-3 text-sm">Stock inventory has been booked and an official Goods Receipt Note (GRN) has been registered.</p>

      <div class="surface-100 border-round p-3 mb-4 text-left">
        <div class="flex justify-content-between mb-2">
          <span class="text-500 font-medium">GRN Number:</span>
          <span class="font-bold text-teal-800">{{ createdReceipt?.receiptNumber || '-' }}</span>
        </div>
        <div class="flex justify-content-between mb-2" *ngIf="createdReceipt?.referenceNumber">
          <span class="text-500 font-medium">Delivery Note Ref:</span>
          <span class="font-semibold">{{ createdReceipt?.referenceNumber }}</span>
        </div>
        <div class="flex justify-content-between mb-2">
          <span class="text-500 font-medium">Total Items:</span>
          <span class="font-semibold">{{ createdReceipt?.totalItems || totalItemsCount() }} lines</span>
        </div>
        <div class="flex justify-content-between">
          <span class="text-500 font-medium">Total Quantity:</span>
          <span class="font-bold text-900">{{ createdReceipt?.totalQuantity || totalQuantitySum() }}</span>
        </div>
      </div>

      <div class="flex flex-column gap-2">
        <p-button
          label="Download / Print GRN PDF"
          icon="pi pi-file-pdf"
          severity="success"
          size="large"
          styleClass="w-full"
          (click)="downloadCreatedGrn()">
        </p-button>
        <div class="flex gap-2">
          <p-button
            [label]="inboundRequest ? 'Back to Warehouse Work' : 'Receive Another Consignment'"
            icon="pi pi-plus"
            severity="secondary"
            [outlined]="true"
            styleClass="w-full"
            (click)="resetForNewReceipt()">
          </p-button>
          <p-button
            label="View Receipts Directory"
            icon="pi pi-list"
            severity="secondary"
            [outlined]="true"
            styleClass="w-full"
            (click)="goToReceipts()">
          </p-button>
        </div>
      </div>
    </div>
  </p-dialog>
</app-page-shell>
